import { Theme } from "@material-ui/core";
import type { Place, ViewState } from "../../types/warehouse.types";
import { CachedDimensions, LOD, PlaceBounds, PlaceRendererConfig } from "../core/types";
import { BaseRenderer } from "../base/BaseRenderer";

/**
 * Renders place labels with Level of Detail (LOD) optimization
 *
 * Pattern: Strategy Pattern (different rendering strategies per LOD level)
 */
export class PlaceLabelRenderer extends BaseRenderer<PlaceRendererConfig> {
  private fontCache = new Map<string, string>();

  constructor(theme: Theme, config: PlaceRendererConfig) {
    super(theme, config);
  }

  /**
   * Calculate appropriate LOD level based on scale
   */
  private calculateLOD(scale: number): LOD {
    if (scale > 0.8) return LOD.HIGH;
    if (scale > 0.4) return LOD.MEDIUM;
    return LOD.LOW;
  }

  /**
   * Get cached font string
   */
  private getFont(size: number, weight: string = 'bold'): string {
    const key = `${weight}_${size.toFixed(1)}`;
    let font = this.fontCache.get(key);

    if (!font) {
      font = `${weight} ${size.toFixed(1)}px ${this.theme.typography.fontFamily}`;
      this.fontCache.set(key, font);
    }

    return font;
  }

  /**
   * Main render method with LOD
   */
  render(
    ctx: CanvasRenderingContext2D,
    place: Place,
    level: number,
    x: number,
    y: number,
    dims: CachedDimensions,
    viewState: ViewState
  ): void {
    const lodLevel = this.calculateLOD(viewState.scale);

    switch (lodLevel) {
      case LOD.HIGH:
        this.renderDetailedLabels(ctx, place, level, x, y, dims, viewState);
        break;
      case LOD.MEDIUM:
        this.renderSimpleLabels(ctx, place, x, y, dims, viewState);
        break;
      case LOD.LOW:
        // Skip labels at low LOD
        break;
    }
  }

  /**
   * Render both level and place labels (HIGH LOD)
   */
  private renderDetailedLabels(
    ctx: CanvasRenderingContext2D,
    place: Place,
    level: number,
    x: number,
    y: number,
    dims: CachedDimensions,
    viewState: ViewState
  ): void {
    // Level label on right face
    if (viewState.scale > this.config.labelMinScale) {
      this.renderLevelLabel(ctx, level, x, y, dims, viewState);
    }

    // Place number on front face
    this.renderPlaceLabel(ctx, place, x, y, dims, viewState);
  }

  /**
   * Render only place label (MEDIUM LOD)
   */
  private renderSimpleLabels(
    ctx: CanvasRenderingContext2D,
    place: Place,
    x: number,
    y: number,
    dims: CachedDimensions,
    viewState: ViewState
  ): void {
    if (viewState.scale > 0.5) {
      this.renderPlaceLabel(ctx, place, x, y, dims, viewState);
    }
  }

  /**
   * Render level number on right face
   */
  private renderLevelLabel(
    ctx: CanvasRenderingContext2D,
    level: number,
    x: number,
    y: number,
    dims: CachedDimensions,
    viewState: ViewState
  ): void {
    const textSize = Math.min(dims.placeHeight * 0.6, 10 * viewState.scale);

    if (textSize <= this.config.labelMinTextSize) {
      return;
    }

    ctx.save();

    // Position on right face
    const centerX = x + dims.placeWidth + dims.isoDepthX / 2;
    const centerY = y + dims.placeHeight / 2 + dims.isoDepthY / 2;

    ctx.translate(centerX, centerY);
    ctx.transform(1, dims.isoDepthY / dims.isoDepthX, 0, 1, 0, 0);

    ctx.font = this.getFont(textSize);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = this.theme.palette.storageUi.backgroundPrimary;
    ctx.fillText(`L${level}`, 0, 0);

    ctx.restore();
  }

  /**
   * Render place number on front face
   */
  private renderPlaceLabel(
    ctx: CanvasRenderingContext2D,
    place: Place,
    x: number,
    y: number,
    dims: CachedDimensions,
    viewState: ViewState
  ): void {
    const textSize = Math.min(dims.placeHeight * 0.6, 10 * viewState.scale);

    if (textSize <= this.config.labelMinTextSize) {
      return;
    }

    const labelColor =
      place.occupied || place.blocked
        ? this.theme.palette.storageUi.highlight
        : this.theme.palette.storageUi.highlightOrange;

    ctx.fillStyle = labelColor;
    ctx.font = this.getFont(textSize);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      `P${place.placeNumber}`,
      x + dims.placeWidth / 2,
      y + dims.placeHeight / 2
    );
  }

  /**
   * Update theme
   */
  updateTheme(theme: Theme): void {
    this.theme = theme;
    this.clearCache();
  }

  /**
   * Clear font cache
   */
  clearCache(): void {
    this.fontCache.clear();
  }

  /**
   * Get cache stats
   */
  getCacheStats(): { fontCacheSize: number } {
    return {
      fontCacheSize: this.fontCache.size,
    };
  }
}
